export interface DigioCallbackResponse {
  error_code?: string;
  message?: string;
  digio_doc_id?: string;
  txn_id?: string;
}

export interface DigioInstance {
  init(): void;
  submit(requestId: string, identifier: string, tokenId?: string): void;
  cancel(): void;
}

declare global {
  interface Window {
    Digio: new (options: {
      environment: "production" | "sandbox";
      callback: (response: DigioCallbackResponse) => void;
      is_iframe?: boolean;
      logo?: string;
      theme?: { primaryColor?: string; secondaryColor?: string };
    }) => DigioInstance;
  }
}

const SCRIPT_PROD_URL = "https://app.digio.in/sdk/v11/digio.js";
const SCRIPT_SANDBOX_URL = "https://ext.digio.in/sdk/v11/digio.js";

let sdkPromise: Promise<void> | null = null;

export function getDigioESignUrl(
  env: "production" | "sandbox",
  docId: string,
  tokenId: string,
  signer: string
) {
  const baseUrl =
    env === "production"
      ? "https://app.digio.in/#/gateway/login/"
      : "https://ext.digio.in/#/gateway/login/";
  let url = baseUrl;
  if (!docId) throw new Error("DocumentId is required");
  url += `${docId}/`;
  const txnId = Math.random().toString(36).slice(2);
  url += `${txnId}/`;
  url += `${signer}`;
  if (!tokenId) throw new Error("tokenId is required");
  url += `?token_id=${tokenId}&redirect_url=${encodeURIComponent(window.location.origin)}/gift-status`;
  return url;
}

export function loadDigioSdk(isProd: boolean): Promise<void> {
  if (window.Digio) return Promise.resolve();
  if (sdkPromise) return sdkPromise;

  sdkPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = isProd ? SCRIPT_PROD_URL : SCRIPT_SANDBOX_URL;
    script.onload = () => resolve();
    script.onerror = () => {
      sdkPromise = null;
      reject(new Error("Digio SDK failed to load"));
    };
    document.head.appendChild(script);
  });

  return sdkPromise;
}

export interface DigioSession {
  /** Digio entity id — KID (KYC request), DID (document), etc. */
  id: string;
  /** Customer email or phone number the entity was created for */
  identifier: string;
  /** GWT access token; skips Digio's first-factor OTP when present */
  tokenId?: string;
}

/**
 * Opens a Digio journey in the SDK's iframe overlay and resolves with the SDK
 * callback response (which carries `error_code` on failure or cancellation).
 *
 * Must be called synchronously from a user event handler, after the SDK has
 * been loaded via `loadDigioSdk` — `init()` runs before `getSession` is awaited
 * so the overlay opens immediately while the session is being created.
 */
export function startDigioFlow(
  getSession: () => Promise<DigioSession>,
  options: { isProd: boolean; logo?: string }
): Promise<DigioCallbackResponse> {
  return new Promise((resolve, reject) => {
    const digio = new window.Digio({
      environment: options.isProd ? "production" : "sandbox",
      is_iframe: true,
      logo: options.logo,
      callback: resolve,
    });
    digio.init();

    getSession()
      .then(({ id, identifier, tokenId }) =>
        digio.submit(id, identifier, tokenId)
      )
      .catch((error) => {
        digio.cancel();
        reject(error);
      });
  });
}
